import { debounce } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

const DEFAULT_DELAY = 400;

export function useDebouncedInput(
  value: string,
  onCommit: (value: string) => void,
  delay: number = DEFAULT_DELAY
): [string, (value: string) => void] {
  const [localValue, setLocalValue] = useState(value);

  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  const committedValueRef = useRef(value);

  const commit = useMemo(
    () =>
      debounce((next: string) => {
        committedValueRef.current = next;
        onCommitRef.current(next);
      }, delay),
    [delay]
  );

  useEffect(() => () => commit.cancel(), [commit]);

  useEffect(() => {
    if (value !== committedValueRef.current) {
      committedValueRef.current = value;
      commit.cancel();
      setLocalValue(value);
    }
  }, [value, commit]);

  const setValue = (next: string) => {
    setLocalValue(next);
    commit(next);
  };

  return [localValue, setValue];
}
